/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, {useState} from 'react'
import clsx from 'clsx'
import { PackagesItemsPerson } from 'types/entities/PackagesItems'
import { translate, currencyNumberFormat } from 'utils/utility'
import type { PackageItem } from 'types/entities/PackageItem'
import { useTranslation } from 'react-i18next'
import { PackageItemItemType } from 'types/enums/PackageItemItemType'
import { useGetGroupedUpsells } from 'utils/upsellsUtils'
import { PackageItemPersonType } from 'types/enums/PackageItemPersonType'
import PackageUpsellSelectableRow from 'pages/Front/Booking/components/PackageUpsell/PackageUpsellSelectableRow'
import { getOfferItemPresentation, isOfferPackageItem } from 'utils/offers/offerItemPresentation'
import { isCreditPackageItem } from 'utils/offers/addItemToCalculationUtils'

type AnyItem = PackageItem | PackagesItemsPerson

interface AddItemToCalculationItemsListProps {
    items: AnyItem[]
    packageItemType?: PackageItemItemType | null
    selectedItemId: number | null
    collapseUnselected?: boolean
    includeInactiveUpsells?: boolean
    onSelect: (item: AnyItem) => void
}

interface Group {
    groupName: string
    items: AnyItem[]
}

const groupItems = (items: AnyItem[]): Group[] => {
    const groupMap = new Map<string, AnyItem[]>()

    for (const item of items) {
        let groupName = 'Package Items'

        if (isOfferPackageItem(item) && (item.type === PackageItemItemType.Upsell || isCreditPackageItem(item))) {
            groupName = item.extra.groupName || 'Upsells'
        } else if (item.person_type === PackageItemPersonType.Child) {
            groupName = 'Children'
        } else if (item.person_type === PackageItemPersonType.Adult) {
            groupName = 'Adults'
        }

        if (!groupMap.has(groupName)) {
            groupMap.set(groupName, [])
        }

        groupMap.get(groupName)!.push(item)
    }

    return Array.from(groupMap.entries()).map(([groupName, groupItems]) => ({
        groupName,
        items: groupItems,
    }))
}

const isUpsellPackageItem = (item: AnyItem): item is PackageItem => {
    return isOfferPackageItem(item) && (item.type === PackageItemItemType.Upsell || isCreditPackageItem(item))
}

interface ItemRowProps {
    item: AnyItem
    selected: boolean
    onSelect: () => void
}

const ItemRow = ({ item, selected, onSelect }: ItemRowProps) => {
    const { t } = useTranslation()
    const presentation = getOfferItemPresentation(item)

    return (
        <PackageUpsellSelectableRow
            descriptionHtml={presentation.custom_text}
            imageSrc={presentation.imageSrc}
            leadingLabel={presentation.leadingLabel}
            maxBadge={
                presentation.maxPersons ? (
                    <span className="UiPill px-2 py-1 text-xs font-medium rounded-full col-start-2 text-center bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 group-hover:bg-white dark:group-hover:bg-slate-700">
                        <span>{t('Max')} {presentation.maxPersons}</span>
                    </span>
                ) : null
            }
            meta={
                <>
                    {presentation.isChild && (
                        <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                            {t('Child')}
                        </span>
                    )}
                    {presentation.subGroupName && (
                        <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                            {presentation.subGroupName}
                        </span>
                    )}
                    {presentation.duration && (
                        <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs text-slate-500 dark:text-slate-400">
                            {presentation.duration} {t('min')}
                        </span>
                    )}
                    {presentation.isKarting && (
                        <span className="rounded-full bg-red-50 dark:bg-red-900/30 px-2 py-0.5 text-xs font-semibold text-red-600 dark:text-red-400">
                            {t('with Karting')}
                        </span>
                    )}
                </>
            }
            minBadge={
                presentation.minPersons ? (
                    <span className="UiPill px-2 py-1 text-xs font-medium rounded-full col-start-1 text-center bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 group-hover:bg-white dark:group-hover:bg-slate-700">
                        <span>{t('Min')} {presentation.minPersons}</span>
                    </span>
                ) : null
            }
            onClick={onSelect}
            price={currencyNumberFormat(item.price)}
            selected={selected}
            title={presentation.title}
        />
    )
}

interface GroupSectionProps {
    groupName: string
    rowsCount: number
    hasAnySelected: boolean
    defaultCollapsed?: boolean
    children: React.ReactNode
}

const GroupSection = ({ groupName, rowsCount, hasAnySelected, defaultCollapsed = false, children }: GroupSectionProps) => {
    const { t } = useTranslation()
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed)

    return (
        <section
            className={clsx(
                'UpsellPriceListSection overflow-hidden rounded-3xl bg-white dark:bg-gray-950 shadow-sm ring-1',
                hasAnySelected
                    ? 'ring-blue-300 dark:ring-blue-700'
                    : 'ring-slate-200 dark:ring-slate-700'
            )}
        >
            <button
                className="sticky top-0 z-10 flex w-full items-center justify-between border-b border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-gray-950/95 px-5 py-4 text-left backdrop-blur"
                type="button"
                onClick={() => setIsCollapsed((prev) => !prev)}
            >
                <div>
                    <h2 className="text-sm font-bold uppercase tracking-wide text-slate-900 dark:text-slate-100">
                        {groupName}
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        {rowsCount === 1
                            ? t('1 item available')
                            : t('{{count}} items available', { count: rowsCount })}
                    </p>
                </div>
                <span
                    className={clsx(
                        'flex size-8 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 transition-transform duration-200',
                        isCollapsed ? '' : 'rotate-180'
                    )}
                >
                    <svg className="size-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                            clipRule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            fillRule="evenodd"
                        />
                    </svg>
                </span>
            </button>
            {!isCollapsed && (
                <div className="divide-y divide-slate-100 dark:divide-slate-800">{children}</div>
            )}
        </section>
    )
}

const AddItemToCalculationItemsList = (props: AddItemToCalculationItemsListProps) => {
    const { t } = useTranslation()
    const { collapseUnselected = false } = props

    const filteredItems = props.items.filter((item) => {
        if (props.packageItemType) {
            if (item.type !== props.packageItemType) {
                return false
            }
        }

        if (
            isUpsellPackageItem(item) &&
            !props.includeInactiveUpsells &&
            item.is_active !== 1
        ) {
            return false
        }

        return true
    })

    const nonUpsellItems = filteredItems.filter((item) => !isUpsellPackageItem(item))
    const upsellItems = filteredItems.filter((item) => isUpsellPackageItem(item))
    const defaultGroups = groupItems(nonUpsellItems)
    const { sortedByGroupNames } = useGetGroupedUpsells(upsellItems)

    if (!filteredItems.length) {
        return (
            <div className="py-8 text-center text-sm text-slate-400 dark:text-slate-500">
                {t('No items available')}
            </div>
        )
    }

    return (
        <div className="UpsellPriceList flex flex-col gap-4">
            {defaultGroups.map((group, idx) => {
                const hasAnySelected = group.items.some((item) => item.id === props.selectedItemId)
                return (
                <GroupSection
                    key={group.groupName ?? `group-${idx}`}
                    groupName={group.groupName}
                    hasAnySelected={hasAnySelected}
                    defaultCollapsed={collapseUnselected && !hasAnySelected}
                    rowsCount={group.items.length}
                >
                    <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                        {group.items.map((item) => (
                            <li className="UpsellPriceList UpsellPriceListSimpleRow" key={item.id}>
                                <ItemRow
                                    item={item}
                                    selected={props.selectedItemId === item.id}
                                    onSelect={() => props.onSelect(item)}
                                />
                            </li>
                        ))}
                    </ul>
                </GroupSection>
                )
            })}

            {sortedByGroupNames.map((group, groupIndex) => {
                const allItemsInGroup = group.subGroups.flatMap((subGroup) =>
                    subGroup.items.flatMap((groupedItem) => groupedItem.items)
                )
                const hasAnySelected = allItemsInGroup.some((item) => item.id === props.selectedItemId)
                return (
                    <GroupSection
                        key={`upsell-group-${group.groupName ?? groupIndex}`}
                        groupName={group.groupName}
                        hasAnySelected={hasAnySelected}
                        defaultCollapsed={collapseUnselected && !hasAnySelected}
                        rowsCount={allItemsInGroup.length}
                    >
                        {group.subGroups.map((subGroup, subGroupIndex) => {
                            const subgroupItems = subGroup.items.flatMap((groupedItem) => groupedItem.items)
                            return (
                                <div key={`upsell-subgroup-${subGroup.subGroupName ?? subGroupIndex}`}>
                                    {subGroup.subGroupName && (
                                        <div className="bg-slate-200 dark:bg-slate-900/50 px-5 py-2 border-b border-slate-100 dark:border-slate-800">
                                            <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-900 dark:text-slate-400">
                                                {subGroup.subGroupName}
                                            </h3>
                                        </div>
                                    )}
                                    <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {subgroupItems.map((item) => (
                                            <li className="sortedByGroupNames UpsellPriceListSimpleRow" key={item.id}>
                                                <ItemRow
                                                    item={item}
                                                    selected={props.selectedItemId === item.id}
                                                    onSelect={() => props.onSelect(item)}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )
                        })}
                    </GroupSection>
                )
            })}
        </div>
    )
}

export default AddItemToCalculationItemsList